import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { styles, colors, fmtMoneyK, useIsMobile } from '../components/UI';
import EventModal from '../components/EventModal';

// Emoji per event type -- matches the server-side TYPE_EMOJI for parity
// between calendar chips and invite emails.
const EVENT_EMOJI = {
  'Luncheon': '🍽️', 'Dinner': '🍷', 'Meeting': '🤝', 'Lunch & Learn': '🎓',
  'Golf': '⛳', 'Presentation': '🎤', 'Trade Show': '🏪',
  'Network Meeting': '🌐', 'Job Walk': '👷', 'Other': '📅'
};

// Calendar / week-ahead view. Two modes: "Week" (7-day strip Mon-Sun) and
// "2 Weeks" (14-day grid). Each cell shows project starts, follow-ups, and
// tasks due that day, all clickable to jump to the source.
export default function Calendar() {
  const { user } = useAuth();
  // Mine / Team scope toggle, mirroring the Dashboard widget. Reps don't
  // see the toggle (their team and mine are the same set). Supervisor /
  // leadership / admin default to Team so they land on their reports'
  // schedule; one click flips to their personal view.
  const canSwitchScope = user.role !== 'rep' && user.role !== 'ops';
  const [scope, setScope] = useState(canSwitchScope ? 'team' : 'mine');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [span, setSpan] = useState(14); // 7 or 14
  const [anchor, setAnchor] = useState(getMonday(new Date())); // start of view
  // Event modal -- null = closed; 'create' = blank form; <event-id> when
  // opening an existing event for view/edit/respond.
  const [eventModal, setEventModal] = useState(null);
  const [openEvent, setOpenEvent] = useState(null);
  const isMobile = useIsMobile();

  const start = isoDate(anchor);
  const end = isoDate(addDays(anchor, span - 1));

  // Refetch trigger -- bumped after create/edit/respond/cancel so the cells
  // refresh without a page reload.
  const [refetchAt, setRefetchAt] = useState(0);
  useEffect(() => {
    const params = { start, end };
    if (scope) params.scope = scope;
    api.calendar(params).then(setData).catch(e => setError(e.message));
  }, [start, end, scope, refetchAt]);

  const openCreateEvent = () => { setOpenEvent(null); setEventModal('create'); };
  const openExistingEvent = async (id) => {
    try {
      const ev = await api.getEvent(id);
      setOpenEvent(ev); setEventModal('view');
    } catch (e) { setError(e.message); }
  };
  const closeEventModal = () => { setEventModal(null); setOpenEvent(null); };
  const onEventSaved = () => { setRefetchAt(Date.now()); };

  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < span; i++) arr.push(addDays(anchor, i));
    return arr;
  }, [anchor, span]);

  // Bucket items by date string
  const byDay = useMemo(() => {
    const m = {};
    days.forEach(d => { m[isoDate(d)] = { projects: [], follow_ups: [], tasks: [], events: [], holidays: [] }; });
    if (!data) return m;
    for (const p of data.projects) {
      const k = String(p.date_planned).slice(0, 10);
      if (m[k]) m[k].projects.push(p);
    }
    for (const f of data.follow_ups) {
      const k = String(f.follow_up_date).slice(0, 10);
      if (m[k]) m[k].follow_ups.push(f);
    }
    for (const t of data.tasks) {
      const k = String(t.due_date).slice(0, 10);
      if (m[k]) m[k].tasks.push(t);
    }
    for (const e of (data.events || [])) {
      const k = String(e.event_date).slice(0, 10);
      if (m[k]) m[k].events.push(e);
    }
    for (const h of (data.holidays || [])) {
      if (m[h.date]) m[h.date].holidays.push(h);
    }
    return m;
  }, [data, days]);

  const todayStr = isoDate(new Date());

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ ...styles.h1, margin: 0 }}>
          Calendar
          {canSwitchScope && (
            <span style={{ color: '#9CA3AF', fontWeight: 400, fontSize: 14, marginLeft: 8 }}>
              · {scope === 'team' ? 'Team' : 'Mine'}
            </span>
          )}
        </h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {canSwitchScope && (
            <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
              {[
                { v: 'team', label: '👥 Team' },
                { v: 'mine', label: '🙋 Mine' }
              ].map(opt => {
                const active = scope === opt.v;
                return (
                  <button
                    key={opt.v}
                    onClick={() => setScope(opt.v)}
                    title={opt.v === 'team'
                      ? "Calendar items for your direct reports"
                      : "Your personal calendar"}
                    style={{
                      padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                      background: active ? colors.charcoal : '#fff',
                      color: active ? '#fff' : colors.charcoal
                    }}
                  >{opt.label}</button>
                );
              })}
            </div>
          )}
          {user.role !== 'ops' && (
            <button style={styles.btn} onClick={openCreateEvent} title="Schedule a luncheon, dinner, golf, trade show, etc.">
              ➕ Schedule Event
            </button>
          )}
          <button style={styles.btnSecondary} onClick={() => setAnchor(addDays(anchor, -span))}>← Prev</button>
          <button style={styles.btnSecondary} onClick={() => setAnchor(getMonday(new Date()))}>This week</button>
          <button style={styles.btnSecondary} onClick={() => setAnchor(addDays(anchor, span))}>Next →</button>
          <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #E5E7EB', marginLeft: 8 }}>
            <button onClick={() => setSpan(7)} style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
              background: span === 7 ? colors.charcoal : '#fff',
              color: span === 7 ? '#fff' : colors.charcoal
            }}>1 Week</button>
            <button onClick={() => setSpan(14)} style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
              background: span === 14 ? colors.charcoal : '#fff',
              color: span === 14 ? '#fff' : colors.charcoal
            }}>2 Weeks</button>
          </div>
        </div>
      </div>

      <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 12 }}>
        {fmtFullDate(anchor)} → {fmtFullDate(addDays(anchor, span - 1))}
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(7, 1fr)',
        gap: 8
      }}>
        {days.map(d => {
          const k = isoDate(d);
          const cell = byDay[k] || { projects: [], follow_ups: [], tasks: [], holidays: [] };
          const isToday = k === todayStr;
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          const isHoliday = cell.holidays.length > 0;
          const isEmpty = cell.tasks.length === 0 && cell.follow_ups.length === 0 && cell.projects.length === 0 && cell.holidays.length === 0 && cell.events.length === 0;
          // Holiday cells get a soft pink tint so they're easy to spot.
          const cellBg = isToday ? '#FEF3C7'
                       : isHoliday ? '#FEF2F2'
                       : isWeekend ? '#F9FAFB'
                       : '#fff';
          const cellBorder = isToday ? '#F59E0B'
                          : isHoliday ? '#FCA5A5'
                          : '#E5E7EB';
          // Mobile: collapse empty days to one short row; let busy days size to content.
          const cellMinHeight = isMobile ? (isEmpty ? 0 : 'auto') : 140;
          return (
            <div key={k} style={{
              background: cellBg,
              border: `1px solid ${cellBorder}`,
              borderRadius: 6, padding: isMobile ? 10 : 8, minHeight: cellMinHeight
            }}>
              <div style={{ fontSize: isMobile ? 13 : 11, fontWeight: 700, color: isToday ? '#92400E' : colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: isEmpty && isMobile ? 0 : 6 }}>
                {isMobile
                  ? d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                  : d.toLocaleDateString('en-US', { weekday: 'short' })}
                {!isMobile && (
                  <span style={{ marginLeft: 4, color: isToday ? '#92400E' : colors.text, fontSize: 13 }}>
                    {d.getDate()}
                  </span>
                )}
                {isToday && <span style={{ marginLeft: 6, fontSize: 11, color: '#92400E' }}>· today</span>}
                {isEmpty && isMobile && <span style={{ marginLeft: 8, fontWeight: 400, color: '#D1D5DB', textTransform: 'none', letterSpacing: 0 }}>—</span>}
              </div>

              {cell.holidays.map((h, i) => (
                <div key={'h' + i} title={h.federal ? 'US Federal Holiday' : (h.state ? `${h.state} state holiday` : 'Observance')}
                     style={chipStyle('#FEE2E2', '#991B1B', isMobile)}>
                  🎉 {h.name}
                </div>
              ))}

              {cell.tasks.map(t => (
                <div key={'t' + t.id} title={t.title} style={chipStyle('#DBEAFE', '#1E40AF', isMobile)}>
                  ✓ {t.title}
                </div>
              ))}

              {cell.follow_ups.map(f => (
                <Link key={'f' + f.id} to={`/accounts/${encodeURIComponent(f.customer)}`}
                      style={{ textDecoration: 'none' }} title={`Follow up with ${f.customer}`}>
                  <div style={chipStyle('#FEE2E2', '#991B1B', isMobile)}>
                    📞 {f.customer}{f.follow_up_type ? ` · ${f.follow_up_type}` : ''}
                  </div>
                </Link>
              ))}

              {cell.projects.map(p => (
                <Link key={'p' + p.id} to={`/accounts/${encodeURIComponent(p.site)}`}
                      style={{ textDecoration: 'none' }} title={`${p.site} (${p.status}) — ${p.account_owner_name || ''}`}>
                  <div style={chipStyle(statusBg(p.status), statusFg(p.status), isMobile)}>
                    🏗 {p.site} · {fmtMoneyK(p.estimated_value)}
                  </div>
                </Link>
              ))}

              {/* Events render with type-specific emoji + purple chip so they
                  stand out from project starts (orange/blue) and follow-ups
                  (red). Click opens EventModal. */}
              {cell.events.map(e => (
                <div
                  key={'e' + e.id}
                  onClick={() => openExistingEvent(e.id)}
                  title={`${e.title}${e.customer ? ' · ' + e.customer : ''}${e.location ? ' · ' + e.location : ''} (by ${e.created_by_name})`}
                  style={{ ...chipStyle('#EDE9FE', '#5B21B6', isMobile), cursor: 'pointer' }}
                >
                  {EVENT_EMOJI[e.event_type] || '📅'} {e.title}
                  {e.start_time && ` · ${fmtTime(e.start_time)}`}
                </div>
              ))}

              {!isMobile && isEmpty && (
                <div style={{ fontSize: 10, color: '#D1D5DB', textAlign: 'center', marginTop: 24 }}>—</div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 16, fontSize: 11, color: colors.textMuted, marginTop: 16, flexWrap: 'wrap' }}>
        <span>📞 follow-up</span>
        <span>🏗 project start</span>
        <span>✓ task</span>
        <span>🍽️ event</span>
        <span>🎉 holiday</span>
      </div>

      {eventModal && (
        <EventModal
          event={openEvent}
          currentUser={user}
          onClose={closeEventModal}
          onSaved={onEventSaved}
        />
      )}
    </>
  );
}

// 24h "HH:MM:SS" -> "12:30 PM"
function fmtTime(t) {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  const hh = h === 0 ? 12 : (h > 12 ? h - 12 : h);
  const ampm = h < 12 ? 'AM' : 'PM';
  return `${hh}:${String(m).padStart(2, '0')} ${ampm}`;
}

const chipStyle = (bg, fg, isMobile) => ({
  display: 'block', background: bg, color: fg,
  fontSize: isMobile ? 13 : 11, fontWeight: 600,
  padding: isMobile ? '6px 10px' : '3px 6px', borderRadius: 4, marginBottom: isMobile ? 6 : 3,
  whiteSpace: isMobile ? 'normal' : 'nowrap',
  overflow: isMobile ? 'visible' : 'hidden',
  textOverflow: isMobile ? 'clip' : 'ellipsis',
  wordBreak: isMobile ? 'break-word' : 'normal'
});

const statusBg = (s) => ({
  Bidding: '#FEF3C7', Won: '#D1FAE5', Active: '#DBEAFE',
  Complete: '#F3F4F6', Lost: '#FEE2E2'
})[s] || '#F3F4F6';

const statusFg = (s) => ({
  Bidding: '#92400E', Won: '#065F46', Active: '#1E40AF',
  Complete: '#6B7280', Lost: '#991B1B'
})[s] || '#1F2937';

function getMonday(d) {
  const x = new Date(d);
  const day = x.getDay() || 7;
  if (day !== 1) x.setDate(x.getDate() - (day - 1));
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function fmtFullDate(d) {
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' });
}
